import React, { createContext, useContext, useState, ReactNode } from 'react';

interface HelpContextType {
  isHelpOpen: boolean;
  openHelp: (topicId?: string, mode?: 'tour' | 'browse') => void;
  closeHelp: () => void;
  initialTopicId?: string;
  initialMode: 'tour' | 'browse';
}

const HelpContext = createContext<HelpContextType | undefined>(undefined);

export const useHelp = () => {
  const context = useContext(HelpContext);
  if (context === undefined) {
    throw new Error('useHelp must be used within a HelpProvider');
  }
  return context;
};

interface HelpProviderProps {
  children: ReactNode;
}

export const HelpProvider: React.FC<HelpProviderProps> = ({ children }) => {
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [initialTopicId, setInitialTopicId] = useState<string | undefined>(undefined);
  const [initialMode, setInitialMode] = useState<'tour' | 'browse'>('browse');

  const openHelp = (topicId?: string, mode: 'tour' | 'browse' = 'browse') => {
    setInitialTopicId(topicId);
    setInitialMode(mode);
    setIsHelpOpen(true);
  };

  const closeHelp = () => {
    setIsHelpOpen(false);
    setInitialTopicId(undefined);
  };

  return (
    <HelpContext.Provider
      value={{
        isHelpOpen,
        openHelp,
        closeHelp,
        initialTopicId,
        initialMode
      }}
    >
      {children}
    </HelpContext.Provider>
  );
};

